import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config';
import { EventBus, GameEvent } from '@/utils/EventBus';

/**
 * Easter egg scene: a truck driving sadly through the English countryside.
 * Triggered by typing BENBENBEN.
 */
export class BenScene extends Phaser.Scene {
  private gfx!: Phaser.GameObjects.Graphics;
  private scrollX = 0;
  private truckX = 0;
  private raindrops: Array<{ x: number; y: number; speed: number; len: number }> = [];
  private clouds: Array<{ x: number; y: number; w: number; h: number; speed: number }> = [];
  private trees: Array<{ x: number; type: 'round' | 'hedge' | 'oak'; h: number }> = [];
  private sheep: Array<{ x: number; frame: number }> = [];
  private elapsed = 0;

  constructor() {
    super({ key: 'BenScene' });
  }

  create(): void {
    // Silence the rave music
    EventBus.emit(GameEvent.MUSIC_STOP);

    this.gfx = this.add.graphics();
    this.scrollX = 0;
    this.truckX = GAME_WIDTH * 0.35;
    this.elapsed = 0;

    // Generate clouds
    this.clouds = [];
    for (let i = 0; i < 8; i++) {
      this.clouds.push({
        x: Math.random() * GAME_WIDTH * 2,
        y: 30 + Math.random() * 100,
        w: 80 + Math.random() * 120,
        h: 25 + Math.random() * 20,
        speed: 5 + Math.random() * 10,
      });
    }

    // Generate rain
    this.raindrops = [];
    for (let i = 0; i < 150; i++) {
      this.raindrops.push({
        x: Math.random() * GAME_WIDTH,
        y: Math.random() * GAME_HEIGHT,
        speed: 200 + Math.random() * 150,
        len: 4 + Math.random() * 8,
      });
    }

    // Generate trees/hedgerows along the road (world-space X positions)
    this.trees = [];
    for (let x = 0; x < 8000; x += 30 + Math.random() * 50) {
      this.trees.push({
        x,
        type: Math.random() < 0.3 ? 'oak' : Math.random() < 0.5 ? 'round' : 'hedge',
        h: 25 + Math.random() * 30,
      });
    }

    // Generate sheep in fields
    this.sheep = [];
    for (let x = 200; x < 8000; x += 150 + Math.random() * 300) {
      this.sheep.push({ x, frame: Math.floor(Math.random() * 2) });
    }

    // Sad message
    const sadText = this.add.text(GAME_WIDTH / 2, 30, '... ben ...', {
      fontFamily: 'serif',
      fontSize: '24px',
      color: '#667788',
      fontStyle: 'italic',
    });
    sadText.setOrigin(0.5, 0.5);
    sadText.setDepth(100);
    sadText.setAlpha(0);

    this.tweens.add({
      targets: sadText,
      alpha: 0.7,
      duration: 3000,
      ease: 'Sine.easeIn',
    });

    // Press ESC to go back
    const hint = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 20, 'press ESC to return', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#556666',
    });
    hint.setOrigin(0.5, 0.5);
    hint.setDepth(100);
    hint.setAlpha(0.5);

    this.input.keyboard!.on('keydown-ESC', () => {
      EventBus.emit(GameEvent.MUSIC_RESUME);
      this.scene.stop('BenScene');
      this.scene.start('MainScene');
      this.scene.launch('UIScene');
    });
  }

  update(_time: number, delta: number): void {
    const dt = delta / 1000;
    this.elapsed += dt;

    // Scroll the world - truck "drives" right
    this.scrollX += 40 * dt;

    const g = this.gfx;
    g.clear();

    this.drawSky(g);
    this.drawDistantHills(g);
    this.drawMiddleHills(g);
    this.drawFields(g);
    this.drawSheep(g);
    this.drawHedgerowsAndTrees(g);
    this.drawRoad(g);
    this.drawTruck(g);
    this.drawRain(g, dt);
    this.drawClouds(g, dt);
  }

  private drawSky(g: Phaser.GameObjects.Graphics): void {
    // Overcast English sky - gradient from grey-blue top to pale grey
    const steps = 20;
    const skyBottom = GAME_HEIGHT * 0.35;
    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      const r = Math.floor(lerp(140, 185, t));
      const gv = Math.floor(lerp(155, 195, t));
      const b = Math.floor(lerp(175, 200, t));
      const y = (skyBottom / steps) * i;
      const h = skyBottom / steps + 1;
      g.fillStyle(rgbToHex(r, gv, b));
      g.fillRect(0, y, GAME_WIDTH, h);
    }
  }

  private drawDistantHills(g: Phaser.GameObjects.Graphics): void {
    // Far hills - very muted blue-green
    g.fillStyle(0x7a9a7a);
    g.beginPath();
    g.moveTo(0, GAME_HEIGHT * 0.38);
    for (let x = 0; x <= GAME_WIDTH; x += 4) {
      const wx = x + this.scrollX * 0.1;
      const y = GAME_HEIGHT * 0.35
        + Math.sin(wx * 0.003) * 20
        + Math.sin(wx * 0.007 + 1) * 12
        + Math.sin(wx * 0.001 + 3) * 25;
      g.lineTo(x, y);
    }
    g.lineTo(GAME_WIDTH, GAME_HEIGHT * 0.5);
    g.lineTo(0, GAME_HEIGHT * 0.5);
    g.closePath();
    g.fillPath();
  }

  private drawMiddleHills(g: Phaser.GameObjects.Graphics): void {
    // Mid hills - deeper green
    g.fillStyle(0x5a8a52);
    g.beginPath();
    g.moveTo(0, GAME_HEIGHT * 0.45);
    for (let x = 0; x <= GAME_WIDTH; x += 4) {
      const wx = x + this.scrollX * 0.3;
      const y = GAME_HEIGHT * 0.42
        + Math.sin(wx * 0.004 + 2) * 18
        + Math.sin(wx * 0.009) * 10
        + Math.sin(wx * 0.002 + 5) * 15;
      g.lineTo(x, y);
    }
    g.lineTo(GAME_WIDTH, GAME_HEIGHT * 0.6);
    g.lineTo(0, GAME_HEIGHT * 0.6);
    g.closePath();
    g.fillPath();
  }

  private drawFields(g: Phaser.GameObjects.Graphics): void {
    // Green fields below the hills
    g.fillStyle(0x4a7a3a);
    g.fillRect(0, GAME_HEIGHT * 0.48, GAME_WIDTH, GAME_HEIGHT * 0.22);

    // Slightly different green patches for field variety
    g.fillStyle(0x3d6e30);
    for (let i = 0; i < 6; i++) {
      const fx = ((i * 300 + 100 - this.scrollX * 0.5) % (GAME_WIDTH + 400)) - 200;
      g.fillRect(fx, GAME_HEIGHT * 0.50, 180, GAME_HEIGHT * 0.12);
    }

    // Stone wall lines across fields
    g.lineStyle(1, 0x888877, 0.4);
    for (let i = 0; i < 4; i++) {
      const wy = GAME_HEIGHT * 0.50 + i * 18;
      g.beginPath();
      g.moveTo(0, wy);
      for (let x = 0; x <= GAME_WIDTH; x += 20) {
        g.lineTo(x, wy + Math.sin((x + this.scrollX * 0.4) * 0.02) * 3);
      }
      g.strokePath();
    }
  }

  private drawSheep(g: Phaser.GameObjects.Graphics): void {
    for (const sheep of this.sheep) {
      const sx = sheep.x - this.scrollX * 0.5;
      if (sx < -20 || sx > GAME_WIDTH + 20) continue;

      const sy = GAME_HEIGHT * 0.52 + Math.sin(sheep.x * 0.01) * 10;

      // Woolly body
      g.fillStyle(0xeeeeee);
      g.fillCircle(sx, sy, 5);
      g.fillCircle(sx + 3, sy - 1, 4);
      g.fillCircle(sx - 3, sy - 1, 4);

      // Head
      g.fillStyle(0x222222);
      g.fillCircle(sx + 5, sy + 1, 2.5);

      // Legs
      g.lineStyle(1, 0x222222);
      g.beginPath();
      g.moveTo(sx - 3, sy + 4);
      g.lineTo(sx - 3, sy + 8);
      g.moveTo(sx + 2, sy + 4);
      g.lineTo(sx + 2, sy + 8);
      g.strokePath();
    }
  }

  private drawHedgerowsAndTrees(g: Phaser.GameObjects.Graphics): void {
    const roadY = GAME_HEIGHT * 0.68;

    for (const tree of this.trees) {
      const tx = tree.x - this.scrollX * 0.8;
      if (tx < -60 || tx > GAME_WIDTH + 60) continue;

      if (tree.type === 'hedge') {
        // Hedgerow - dark green rectangle cluster
        g.fillStyle(0x2a5520);
        g.fillRect(tx - 15, roadY - 30 - tree.h * 0.3, 30, tree.h * 0.6);
        g.fillStyle(0x1e4418);
        g.fillCircle(tx - 8, roadY - 30 - tree.h * 0.3, 10);
        g.fillCircle(tx + 8, roadY - 30 - tree.h * 0.3, 10);
        g.fillCircle(tx, roadY - 30 - tree.h * 0.3 - 5, 8);
      } else if (tree.type === 'oak') {
        // Big oak tree
        // Trunk
        g.fillStyle(0x4a3520);
        g.fillRect(tx - 3, roadY - 30, 6, 25);
        // Canopy
        g.fillStyle(0x2d5a1e);
        g.fillCircle(tx, roadY - 40, 18);
        g.fillCircle(tx - 10, roadY - 35, 14);
        g.fillCircle(tx + 10, roadY - 35, 14);
        g.fillStyle(0x1e4415);
        g.fillCircle(tx + 5, roadY - 45, 12);
      } else {
        // Round bush
        g.fillStyle(0x2a5a20);
        g.fillCircle(tx, roadY - 25, tree.h * 0.4);
        g.fillStyle(0x1e4418);
        g.fillCircle(tx + 4, roadY - 28, tree.h * 0.3);
      }
    }
  }

  private drawRoad(g: Phaser.GameObjects.Graphics): void {
    const roadY = GAME_HEIGHT * 0.68;
    const roadH = 50;

    // Road verge (muddy edge)
    g.fillStyle(0x6b7a4a);
    g.fillRect(0, roadY - 5, GAME_WIDTH, roadH + 10);

    // Road surface - grey tarmac
    g.fillStyle(0x555555);
    g.fillRect(0, roadY, GAME_WIDTH, roadH);

    // Darker patches / wear
    g.fillStyle(0x4a4a4a);
    for (let i = 0; i < 10; i++) {
      const px = ((i * 200 - this.scrollX) % (GAME_WIDTH + 300)) - 100;
      g.fillRect(px, roadY + 10, 80, 30);
    }

    // Centre line - dashed white
    g.lineStyle(2, 0xcccccc, 0.7);
    const dashLen = 30;
    const gapLen = 20;
    const total = dashLen + gapLen;
    for (let x = -total; x < GAME_WIDTH + total; x += total) {
      const dx = ((x - this.scrollX) % (GAME_WIDTH + total * 2)) - total;
      g.beginPath();
      g.moveTo(dx, roadY + roadH / 2);
      g.lineTo(dx + dashLen, roadY + roadH / 2);
      g.strokePath();
    }

    // Edge lines
    g.lineStyle(1, 0x999999, 0.5);
    g.beginPath();
    g.moveTo(0, roadY + 2);
    g.lineTo(GAME_WIDTH, roadY + 2);
    g.moveTo(0, roadY + roadH - 2);
    g.lineTo(GAME_WIDTH, roadY + roadH - 2);
    g.strokePath();

    // Below the road - more field/grass
    g.fillStyle(0x3a6a2a);
    g.fillRect(0, roadY + roadH + 5, GAME_WIDTH, GAME_HEIGHT - roadY - roadH);

    // Fence posts along bottom
    g.fillStyle(0x5a4a3a);
    for (let i = 0; i < 30; i++) {
      const fx = ((i * 60 - this.scrollX * 0.8) % (GAME_WIDTH + 120)) - 60;
      g.fillRect(fx, roadY + roadH + 8, 3, 15);
    }
    // Fence wire
    g.lineStyle(1, 0x777766, 0.5);
    g.beginPath();
    g.moveTo(0, roadY + roadH + 14);
    g.lineTo(GAME_WIDTH, roadY + roadH + 14);
    g.moveTo(0, roadY + roadH + 20);
    g.lineTo(GAME_WIDTH, roadY + roadH + 20);
    g.strokePath();
  }

  private drawTruck(g: Phaser.GameObjects.Graphics): void {
    const roadY = GAME_HEIGHT * 0.68;
    const tx = this.truckX;
    const ty = roadY + 5;

    // Gentle bobbing
    const bob = Math.sin(this.elapsed * 3) * 1.5;

    // Shadow
    g.fillStyle(0x333333);
    g.fillEllipse(tx + 20, ty + 40, 80, 8);

    // --- Truck body ---
    // Main cargo area (box truck)
    g.fillStyle(0x3366aa);
    g.fillRect(tx - 15, ty - 25 + bob, 55, 35);

    // Cargo area side detail - panel lines
    g.lineStyle(1, 0x2a5590, 0.8);
    g.beginPath();
    g.moveTo(tx - 15, ty - 10 + bob);
    g.lineTo(tx + 40, ty - 10 + bob);
    g.strokePath();

    // Mud splatter on cargo
    g.fillStyle(0x5a5040);
    g.fillCircle(tx - 10, ty + 7 + bob, 3);
    g.fillCircle(tx - 5, ty + 5 + bob, 2);
    g.fillCircle(tx + 5, ty + 8 + bob, 2);

    // Cab
    g.fillStyle(0x2255aa);
    g.fillRect(tx + 38, ty - 18 + bob, 22, 28);

    // Windscreen
    g.fillStyle(0x99bbdd);
    g.fillRect(tx + 42, ty - 15 + bob, 15, 12);

    // Windscreen reflection
    g.fillStyle(0xbbddff);
    g.fillRect(tx + 44, ty - 14 + bob, 4, 10);

    // Sad face in windscreen (driver)
    g.fillStyle(0xd4a060);
    g.fillCircle(tx + 52, ty - 8 + bob, 3);
    // Sad mouth
    g.lineStyle(1, 0x553322);
    g.beginPath();
    g.arc(tx + 52, ty - 5.5 + bob, 1.5, Math.PI, 0, true);
    g.strokePath();

    // Headlight
    g.fillStyle(0xffeeaa);
    g.fillCircle(tx + 60, ty + 3 + bob, 2.5);

    // Bumper
    g.fillStyle(0x888888);
    g.fillRect(tx + 38, ty + 9 + bob, 24, 3);

    // Rear light
    g.fillStyle(0xff3333);
    g.fillRect(tx - 16, ty + 2 + bob, 3, 5);

    // Exhaust pipe
    g.fillStyle(0x555555);
    g.fillRect(tx - 18, ty + 5 + bob, 5, 3);

    // Exhaust puff
    const puffAlpha = 0.2 + Math.sin(this.elapsed * 2) * 0.1;
    g.fillStyle(0x888888);
    for (let i = 0; i < 3; i++) {
      const px = tx - 22 - i * 8 - Math.sin(this.elapsed + i) * 3;
      const py = ty + 4 + bob - i * 3;
      g.setAlpha(puffAlpha - i * 0.05);
      g.fillCircle(px, py, 3 + i * 1.5);
    }
    g.setAlpha(1);

    // Wheels
    g.fillStyle(0x222222);
    g.fillCircle(tx + 0, ty + 12 + bob, 6);
    g.fillCircle(tx + 45, ty + 12 + bob, 6);
    // Hubcaps
    g.fillStyle(0x666666);
    g.fillCircle(tx + 0, ty + 12 + bob, 2.5);
    g.fillCircle(tx + 45, ty + 12 + bob, 2.5);

    // Wheel spinning detail
    const rot = this.elapsed * 8;
    g.lineStyle(1, 0x555555);
    for (const wx of [tx + 0, tx + 45]) {
      for (let a = 0; a < 3; a++) {
        const angle = rot + (a * Math.PI * 2) / 3;
        g.beginPath();
        g.moveTo(wx, ty + 12 + bob);
        g.lineTo(wx + Math.cos(angle) * 5, ty + 12 + bob + Math.sin(angle) * 5);
        g.strokePath();
      }
    }
  }

  private drawRain(g: Phaser.GameObjects.Graphics, dt: number): void {
    g.lineStyle(1, 0x8899aa, 0.3);
    for (const drop of this.raindrops) {
      drop.y += drop.speed * dt;
      drop.x -= 15 * dt; // slight wind
      if (drop.y > GAME_HEIGHT) {
        drop.y = -drop.len;
        drop.x = Math.random() * GAME_WIDTH;
      }
      if (drop.x < 0) drop.x = GAME_WIDTH;

      g.beginPath();
      g.moveTo(drop.x, drop.y);
      g.lineTo(drop.x - 2, drop.y + drop.len);
      g.strokePath();
    }
  }

  private drawClouds(g: Phaser.GameObjects.Graphics, dt: number): void {
    for (const cloud of this.clouds) {
      cloud.x -= cloud.speed * dt;
      if (cloud.x + cloud.w < -50) {
        cloud.x = GAME_WIDTH + 50;
        cloud.y = 30 + Math.random() * 100;
      }

      // Draw puffy cloud
      g.fillStyle(0xbbbbbb);
      g.setAlpha(0.6);
      g.fillEllipse(cloud.x, cloud.y, cloud.w, cloud.h);
      g.fillEllipse(cloud.x - cloud.w * 0.25, cloud.y + 5, cloud.w * 0.6, cloud.h * 0.7);
      g.fillEllipse(cloud.x + cloud.w * 0.2, cloud.y - 3, cloud.w * 0.5, cloud.h * 0.8);
      g.setAlpha(1);
    }
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function rgbToHex(r: number, g: number, b: number): number {
  return (r << 16) | (g << 8) | b;
}
